import { useCallback, useEffect, useRef, useState } from 'react';
import { UserService } from '../services/user/UserService';
import { UserStatus } from '../common/enums/auth';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { localStorageService } from '../services/localStorageService/localStorageService';

const userService = new UserService();
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export const useUserStatus = () => {
  const [isUserPhoneConfirmed, setIsUserPhoneConfirmed] = useState<
    boolean | null
  >(null);
  const [useBiometric, setUseBiometric] = useState<boolean | null>(null);
  const [hasShownBiometricDrawer, setHasShownBiometricDrawer] = useState<boolean>(
    localStorageService().getHasShownBiometricDrawer() || false
  );
  const { pathname } = useLocation();
  const retryCount = useRef(0);

  const checkUserStatus = useCallback(async () => {
    if (
      pathname !== `/${ROUTES.LOGIN}` &&
      !pathname.includes(`/${ROUTES.RESET_PASSWORD}`)
    ) {
      try {
        const response = await userService.getUserBalance();
        retryCount.current = 0;
        setIsUserPhoneConfirmed(response.status === UserStatus.ACTIVE);
        setUseBiometric(response.useBiometric || false);
      } catch {
        if (retryCount.current < MAX_RETRIES) {
          retryCount.current += 1;
          setTimeout(checkUserStatus, RETRY_DELAY_MS * retryCount.current);
        } else {
          // All retries exhausted — treat as unconfirmed so the page doesn't spin forever
          retryCount.current = 0;
          setIsUserPhoneConfirmed(false);
        }
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (!isUserPhoneConfirmed) {
      checkUserStatus();
    }
  }, [checkUserStatus]);

  const setHasShownBiometricDrawerWrapper = (value: boolean) => {
    setHasShownBiometricDrawer(value);
    localStorageService().setHasShownBiometricDrawer(value);
  };

  return {
    isUserPhoneConfirmed,
    checkUserStatus,
    useBiometric,
    setUseBiometric,
    hasShownBiometricDrawer,
    setHasShownBiometricDrawer: setHasShownBiometricDrawerWrapper,
  };
};
